<template>
  <router-link 
    :to="`/toy/${toy.path || toy.id}`" 
    class="toy-card"
    :aria-label="`查看 ${toy.title}`"
  >
    <div class="card-header">
      <div class="category-badge" :class="`category-${toy.category}`">
        <span class="category-icon">{{ getCategoryIcon(toy.category) }}</span>
        <span class="category-name">{{ getCategoryName(toy.category) }}</span>
      </div>
      <div class="difficulty-badge" :class="`difficulty-${toy.difficulty}`">
        {{ getDifficultyLabel(toy.difficulty) }}
      </div>
    </div>

    <div class="card-body">
      <h3 class="toy-title">{{ toy.title }}</h3>
      <p class="toy-description">{{ toy.description }}</p>
      
      <div class="toy-tags">
        <span 
          v-for="tag in displayTags" 
          :key="tag"
          class="tag"
        >
          {{ tag }}
        </span>
      </div>
    </div>

    <div class="card-footer">
      <div class="stat-item">
        <span class="stat-icon">👍</span>
        <span class="stat-value">{{ formatNumber(toy.likes || 0) }}</span>
      </div>
      <div class="stat-item">
        <span class="stat-icon">👁️</span>
        <span class="stat-value">{{ formatNumber(toy.views || 0) }}</span>
      </div>
      <div class="stat-item">
        <span class="stat-icon">💾</span>
        <span class="stat-value">{{ formatNumber(toy.saves || 0) }}</span>
      </div>
    </div>
  </router-link>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  toy: {
    type: Object,
    required: true
  },
  maxTags: {
    type: Number,
    default: 3
  }
})

const categoryConfig = {
  mathematics: { name: '数学', icon: '📐' },
  physics: { name: '物理', icon: '⚛️' },
  hydrology: { name: '水文学', icon: '💧' },
  'fluid-dynamics': { name: '流体力学', icon: '🌊' },
  chemistry: { name: '化学', icon: '🧪' },
  biology: { name: '生物', icon: '🧬' },
  'machine-learning': { name: '机器学习', icon: '🤖' },
  statistics: { name: '统计学', icon: '📊' },
  'computer-science': { name: '计算机科学', icon: '💻' }
}

const difficultyConfig = {
  basic: '基础',
  intermediate: '中级',
  advanced: '高级'
}

const displayTags = computed(() => {
  if (!props.toy.tags) return []
  return props.toy.tags.slice(0, props.maxTags)
})

function getCategoryIcon(category) {
  return categoryConfig[category]?.icon || '📚'
}

function getCategoryName(category) {
  return categoryConfig[category]?.name || category
}

function getDifficultyLabel(difficulty) {
  return difficultyConfig[difficulty] || difficulty
}

function formatNumber(num) {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M'
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K'
  }
  return num.toString()
}
</script>

<style scoped>
.toy-card {
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  text-decoration: none;
  color: inherit;
  height: 100%;
  cursor: pointer;
}

.toy-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12);
}

.toy-card:active {
  transform: translateY(-2px);
}

/* 卡片头部 */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  gap: 0.5rem;
}

.category-badge {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  flex: 1;
  min-width: 0;
}

.category-icon {
  font-size: 1rem;
  flex-shrink: 0;
}

.category-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.category-mathematics {
  background: #E3F2FD;
  color: #1976D2;
}

.category-physics {
  background: #F3E5F5;
  color: #7B1FA2;
}

.category-hydrology {
  background: #E0F2F1;
  color: #00796B;
}

.category-fluid-dynamics {
  background: #E1F5FE;
  color: #0277BD;
}

.category-chemistry {
  background: #FFF3E0;
  color: #E65100;
}

.category-biology {
  background: #F1F8E9;
  color: #558B2F;
}

.category-machine-learning {
  background: #E8EAF6;
  color: #3949AB;
}

.category-statistics {
  background: #FFFDE7;
  color: #F9A825;
}

.category-computer-science {
  background: #EDE7F6;
  color: #6A1B9A;
}

.difficulty-badge {
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
}

.difficulty-basic {
  background: #E8F5E9;
  color: #2E7D32;
}

.difficulty-intermediate {
  background: #FFF3E0;
  color: #EF6C00;
}

.difficulty-advanced {
  background: #FFEBEE;
  color: #C62828;
}

/* 卡片主体 */
.card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.toy-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #333;
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.toy-description {
  font-size: 0.875rem;
  color: #666;
  line-height: 1.5;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;
}

.toy-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: auto;
}

.tag {
  padding: 0.25rem 0.625rem;
  background: #F5F5F5;
  color: #666;
  border-radius: 12px;
  font-size: 0.75rem;
  white-space: nowrap;
}

/* 卡片底部 */
.card-footer {
  display: flex;
  gap: 1rem;
  padding-top: 1rem;
  margin-top: 1rem;
  border-top: 1px solid #f0f0f0;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.875rem;
  color: #777;
}

.stat-icon {
  font-size: 1rem;
}

.stat-value {
  font-weight: 500;
}

/* 平板设备 */
@media (max-width: 1024px) and (min-width: 641px) {
  .toy-card {
    padding: 1.125rem;
  }

  .toy-title {
    font-size: 1.0625rem;
  }
}

/* 响应式网格布局支持 */
@media (max-width: 640px) {
  .toy-card {
    padding: 1rem;
    min-height: 240px;
  }

  .card-header {
    flex-wrap: wrap;
  }

  .category-badge,
  .difficulty-badge {
    font-size: 0.75rem;
    padding: 0.375rem 0.75rem;
  }

  .toy-title {
    font-size: 1rem;
  }

  .toy-description {
    font-size: 0.8125rem;
    -webkit-line-clamp: 2;
  }

  .card-footer {
    gap: 0.875rem;
  }

  .stat-item {
    font-size: 0.8125rem;
  }
}

/* 小屏幕移动设备 - 增大触摸目标 */
@media (max-width: 480px) {
  .toy-card {
    padding: 1rem;
    min-height: 220px;
    -webkit-tap-highlight-color: transparent;
  }

  .toy-card:active {
    transform: scale(0.98);
  }

  .category-badge,
  .difficulty-badge {
    font-size: 0.7rem;
    padding: 0.4rem 0.7rem;
  }

  .toy-title {
    font-size: 0.9375rem;
  }

  .toy-description {
    font-size: 0.8rem;
  }

  .card-footer {
    gap: 0.75rem;
    padding-top: 0.875rem;
    margin-top: 0.875rem;
  }

  .stat-item {
    font-size: 0.8rem;
  }

  .stat-icon {
    font-size: 1.125rem;
  }
}

/* 确保在网格布局中的卡片高度一致 */
.toy-card {
  min-height: 280px;
}
</style>
